import i18n from '../../i18n';
import { BaseApiClient } from './base-client';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Получить текущий язык UI пользователя для передачи в `pg_language` (FreedomPay).
 * Маппинг: RU→ru, KK→kk, EN→en, прочее→ru.
 * FP принимает: ru, en, kk, kg, uz.
 */
function currentLang(): 'ru' | 'en' | 'kk' {
  const raw = (i18n.language || '').toLowerCase();
  if (raw.startsWith('kk') || raw.startsWith('kz')) return 'kk';
  if (raw.startsWith('en')) return 'en';
  return 'ru';
}

/**
 * Payment API - методы для платежей и подписок
 */
export class PaymentApi extends BaseApiClient {

  // ===== PAYMENT STATUS =====

  /**
   * Check payment status by ID
   */
  async checkStatus(paymentId: string): Promise<any> {
    const response = await this.client.get(`/api/payment/status/${paymentId}`);
    return response.data;
  }

  /**
   * Get payment history
   */
  async getHistory(limit: number = 10): Promise<any> {
    const response = await this.client.get('/api/payment/history', {
      params: { limit },
    });
    return response.data;
  }

  // ===== YOOKASSA PAYMENTS =====

  /**
   * Create subscription payment.
   * Передаём `language` для FreedomPay pg_language (страница оплаты на нужном языке).
   * `email` (опционально) — backend сохранит в users.receipt_email если поле
   * было пустым, чтобы в следующих оплатах не запрашивать снова.
   */
  async createPayment(data: { tier: string; period: string; paymentMethod?: string; email?: string }): Promise<any> {
    const response = await this.client.post('/api/payment/create', { ...data, language: currentLang() });
    return response.data;
  }

  /**
   * Create tokens purchase payment.
   * `email` ведёт себя так же как в createPayment — backend persistит для
   * последующих чеков.
   */
  async createTokensPayment(data: { tokens: number; amount: number; teamId?: string; paymentMethod?: string; email?: string }): Promise<any> {
    const teamId = sanitizeTeamId(data.teamId);
    const response = await this.client.post('/api/payment/create-tokens', { ...data, teamId, language: currentLang() });
    return response.data;
  }

  /**
   * Create subscription upgrade payment
   */
  async createUpgradePayment(data: { targetTier: string; targetPeriod?: string; paymentMethod?: string; email?: string }): Promise<any> {
    const response = await this.client.post('/api/payment/create-upgrade', { ...data, language: currentLang() });
    return response.data;
  }

  /**
   * Get upgrade price calculation
   */
  async getUpgradePrice(tier: string, period?: string): Promise<any> {
    const params = period ? `?period=${period}` : '';
    const response = await this.client.get(`/api/payment/upgrade-price/${tier}${params}`);
    return response.data;
  }

  // ===== TELEGRAM STARS PAYMENTS =====

  /**
   * Get Stars configuration (availability, exchange rate)
   */
  async getStarsConfig(): Promise<any> {
    const response = await this.client.get('/api/payment/stars/config');
    return response.data;
  }

  /**
   * Send Stars invoice (for desktop/web fallback)
   */
  async sendStarsInvoice(data: {
    type: 'subscription' | 'tokens' | 'upgrade';
    tier?: string;
    period?: string;
    tokens?: number;
    amount?: number;
    targetTier?: string;
    teamId?: string;
  }): Promise<any> {
    const teamId = sanitizeTeamId(data.teamId);
    const response = await this.client.post('/api/payment/stars/send-invoice', { ...data, teamId });
    return response.data;
  }

  /**
   * Create Stars subscription payment
   */
  async createStarsSubscription(data: { tier: string; period: string }): Promise<any> {
    const response = await this.client.post('/api/payment/stars/create-subscription', data);
    return response.data;
  }

  /**
   * Create Stars tokens purchase
   */
  async createStarsTokens(data: { tokens: number; amount: number; teamId?: string }): Promise<any> {
    const teamId = sanitizeTeamId(data.teamId);
    const response = await this.client.post('/api/payment/stars/create-tokens', { ...data, teamId });
    return response.data;
  }

  /**
   * Create Stars subscription upgrade
   */
  async createStarsUpgrade(data: { targetTier: string }): Promise<any> {
    const response = await this.client.post('/api/payment/stars/create-upgrade', data);
    return response.data;
  }

  /**
   * Cancel payment (YooKassa)
   */
  async cancelPayment(paymentId: string): Promise<any> {
    const response = await this.client.post(`/api/payment/cancel/${paymentId}`);
    return response.data;
  }

  /**
   * Отключить автопродление подписки (FreedomPay recurring profile).
   * Подписка остаётся активной до expires_at, но в этот день не спишет автоматически.
   */
  async cancelAutoRenewal(): Promise<any> {
    const response = await this.client.post('/api/payment/cancel-auto-renewal');
    return response.data;
  }

  /**
   * Cancel Stars payment
   */
  async cancelStarsPayment(paymentId: string): Promise<any> {
    const response = await this.client.delete(`/api/payment/stars/${paymentId}`);
    return response.data;
  }

  // ── Team Plan ──

  async createTeamSubscription(data: { seats: number; period: string; name?: string; avatarUrl?: string; extraTokens?: number }): Promise<any> {
    const response = await this.client.post('/api/payment/create-team-subscription', { ...data, language: currentLang() });
    return response.data;
  }

  async createStarsTeamSubscription(data: { seats: number; period: string; name?: string; avatarUrl?: string; extraTokens?: number }): Promise<any> {
    const response = await this.client.post('/api/payment/stars/create-team-subscription', data);
    return response.data;
  }

  async renewTeamSubscription(data: { teamId: string; period?: string; email?: string }): Promise<any> {
    const response = await this.client.post('/api/payment/renew-team-subscription', { ...data, language: currentLang() });
    return response.data;
  }

  async upgradeTeamSeats(data: { teamId: string; additionalSeats: number; email?: string }): Promise<any> {
    const response = await this.client.post('/api/payment/upgrade-team-seats', { ...data, language: currentLang() });
    return response.data;
  }

  async getTeamPeriodUpgradePrice(teamId: string): Promise<any> {
    const response = await this.client.get(`/api/payment/upgrade-team-period-price/${teamId}`);
    return response.data;
  }

  async upgradeTeamPeriod(data: { teamId: string; email?: string }): Promise<any> {
    const response = await this.client.post('/api/payment/upgrade-team-period', { ...data, language: currentLang() });
    return response.data;
  }
}
